import { AlertCircle, CheckCircle2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import { cashSettlementApi } from './cashSettlementApi';

const DENOMINATIONS = [
  { label: '₹2000', value: 2000 },
  { label: '₹500', value: 500 },
  { label: '₹200', value: 200 },
  { label: '₹100', value: 100 },
  { label: '₹50', value: 50 },
  { label: '₹20', value: 20 },
  { label: '₹10', value: 10 },
  { label: '₹5', value: 5 },
  { label: '₹2', value: 2 },
  { label: '₹1', value: 1 },
];

/** Returns YYYY-MM-DD for today (local). */
const getTodayYYYYMMDD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Returns YYYY-MM-DD from shift_start_timestamp (ISO string). */
const getDateFromShiftTimestamp = (ts) => {
  if (!ts) return null;
  if (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}/.test(ts)) return ts.slice(0, 10);
  try {
    return new Date(ts).toISOString().slice(0, 10);
  } catch {
    return null;
  }
};

/** Format timestamp to time string e.g. "9:00 AM", "5:00 PM". */
const formatTimeFromTimestamp = (ts) => {
  if (!ts) return '--';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return '--';
  }
};

const SafeDropForm = () => {
  // Log screen view
  useScreenLogging('SafeDropForm', {
    component: 'SafeDropForm',
    screenType: 'safe-drop',
  });

  const [selectedSite, setSelectedSite] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [shiftDate, setShiftDate] = useState('');
  const [sites, setSites] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [denominationCounts, setDenominationCounts] = useState({});
  const [notes, setNotes] = useState('');
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');
  const [signatureCanvas, setSignatureCanvas] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [expectedCash, setExpectedCash] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState({ success: '', error: '' });
  
  const canvasRef = useRef(null);
  const signatureRef = useRef(null);

  // Initialize denomination counts
  useEffect(() => {
    const initialCounts = {};
    DENOMINATIONS.forEach(denom => {
      initialCounts[denom.value] = 0;
    });
    setDenominationCounts(initialCounts);
  }, []);

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites) ? response.sites : Array.isArray(response) ? response : [];
        setSites(list.sort((a, b) => (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')));
        
        // Log successful API call
        loggingService.logInfo(
          'safe_drop_load_sites',
          `Successfully loaded ${list.length} sites for safe drop`,
          {
            endpoint: '/sites',
            method: 'GET',
            siteCount: list.length,
          }
        ).catch(logError => {
          console.warn('[SafeDropForm] Failed to log event:', logError);
        });
      } catch (error) {
        console.error('Error fetching sites:', error);
        
        // Log API error
        loggingService.logError(
          'safe_drop_load_sites_error',
          `Failed to load sites for safe drop: ${error.message || 'Unknown error'}`,
          error,
          {
            endpoint: '/sites',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[SafeDropForm] Failed to log error:', logError);
        });
      }
    };
    fetchSites();
  }, []);

  // Prefill Site, Shift and date from current user attendance (GET /attendance/get-current/v2)
  useEffect(() => {
    const applyCurrentShift = async () => {
      try {
        const result = await cashSettlementApi.getCurrentShiftData();
        if (result.success && result.shift) {
          const shift = result.shift;
          setShifts(result.shifts || [shift]);
          setSelectedShiftId(shift.ss_id);
          setSelectedSite(shift.site_id || '');
          const dateStr = getDateFromShiftTimestamp(shift.shift_start_timestamp);
          if (dateStr) setShiftDate(dateStr);
          
          // Log successful API call
          loggingService.logInfo(
            'safe_drop_get_current_shift',
            `Successfully loaded current shift ${shift.ss_id} for safe drop`,
            {
              endpoint: '/attendance/get-current/v2',
              method: 'GET',
              shiftId: shift.ss_id,
              siteId: shift.site_id,
            }
          ).catch(logError => {
            console.warn('[SafeDropForm] Failed to log event:', logError);
          });
        } else {
          setShifts([]);
          setSelectedShiftId('');
        }
      } catch (error) {
        console.warn(' SafeDropForm: Could not prefill from current attendance:', error?.message || error);
        setShifts([]);
        setSelectedShiftId('');
        
        // Log API error
        loggingService.logError(
          'safe_drop_get_current_shift_error',
          `Failed to get current shift for safe drop: ${error.message || 'Unknown error'}`,
          error,
          {
            endpoint: '/attendance/get-current/v2',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[SafeDropForm] Failed to log error:', logError);
        });
      }
    };
    applyCurrentShift();
  }, []);

  // Compute expected cash from current shift in state (no extra API call)
  useEffect(() => {
    if (!selectedShiftId || !shifts.length) {
      setExpectedCash(0);
      return;
    }
    const shift = shifts.find((s) => s.ss_id === selectedShiftId);
    if (!shift) {
      setExpectedCash(0);
      return;
    }
    const openingFloat = parseFloat(shift.opening_float || 0);
    const totalCashSales = parseFloat(shift.total_cash_sales || 0);
    setExpectedCash(openingFloat + totalCashSales);
  }, [selectedShiftId, shifts]);

  // Initialize signature canvas
  useEffect(() => {
    if (signatureType === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setSignatureCanvas(canvas);
    }
  }, [signatureType]);

  // Calculate total counted cash
  const totalCountedCash = DENOMINATIONS.reduce((sum, denom) => {
    return sum + (denom.value * (denominationCounts[denom.value] || 0));
  }, 0);

  // Calculate variance
  const variance = totalCountedCash - expectedCash;

  // Handle signature drawing
  const startDrawing = (e) => {
    if (signatureType !== 'draw') return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing || signatureType !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setTypedSignature('');
  };

  const getSignatureData = () => {
    if (signatureType === 'draw' && canvasRef.current) {
      return canvasRef.current.toDataURL('image/png');
    } else if (signatureType === 'type' && typedSignature.trim()) {
      return typedSignature.trim();
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log(' SafeDropForm: Submit button clicked');
    console.log(' SafeDropForm: Selected Site:', selectedSite);
    console.log(' SafeDropForm: Selected Shift ID:', selectedShiftId);
    
    if (!selectedSite || !selectedShiftId) {
      console.warn(' SafeDropForm: Validation failed - missing site or shift');
      setSubmitFeedback({ error: 'Please select a site and shift', success: '' });
      return;
    }

    // Signature is optional for safe drop (drawer count)
    const signature = getSignatureData();
    console.log(' SafeDropForm: Signature provided:', !!signature);

    setIsSubmitting(true);
    setSubmitFeedback({ success: '', error: '' });

    try {
      // Prepare denominations object with string keys as required by API
      const denominations = {};
      DENOMINATIONS.forEach(denom => {
        const count = denominationCounts[denom.value] || 0;
        if (count > 0) {
          denominations[denom.value.toString()] = count;
        }
      });

      console.log(' SafeDropForm: Denominations:', denominations);
      console.log(' SafeDropForm: Total Counted Cash:', totalCountedCash);

      // Create drawer count (safe drop) data
      const drawerData = {
        ss_id: selectedShiftId,
        drawer_id: 'drawer-1', // Default drawer ID, can be made configurable
        denominations: denominations,
        total_amount: totalCountedCash,
      };

      console.log(' SafeDropForm: Calling createDrawerCount API with data:', drawerData);
      const result = await cashSettlementApi.createDrawerCount(drawerData);
      console.log(' SafeDropForm: createDrawerCount API Response:', result);
      console.log(' SafeDropForm: Success:', result.success);

      if (result.success) {
        console.log(' SafeDropForm: Drawer count created successfully');
        
        // Log successful API call
        loggingService.logInfo(
          'safe_drop_create_drawer_count',
          `Successfully created drawer count for shift ${selectedShiftId}`,
          {
            endpoint: '/cash/drawers',
            method: 'POST',
            shiftId: selectedShiftId,
            totalAmount: totalCountedCash,
            drawerId: drawerData.drawer_id,
          }
        ).catch(logError => {
          console.warn('[SafeDropForm] Failed to log event:', logError);
        });
        
        // If signature is provided, update the cash shift with signature
        if (signature) {
          console.log(' SafeDropForm: Updating cash shift with signature');
          const shiftUpdateData = {
            manager_signature_digital: signature,
          };
          const updateResult = await cashSettlementApi.updateCashShift(selectedShiftId, shiftUpdateData);
          console.log(' SafeDropForm: updateCashShift API Response:', updateResult);
          
          // Log successful API call
          if (updateResult.success) {
            loggingService.logInfo(
              'safe_drop_update_cash_shift',
              `Successfully updated cash shift ${selectedShiftId} with signature`,
              {
                endpoint: `/cash/shifts/${selectedShiftId}`,
                method: 'PUT',
                shiftId: selectedShiftId,
                hasSignature: true,
              }
            ).catch(logError => {
              console.warn('[SafeDropForm] Failed to log event:', logError);
            });
          }
        }

        setSubmitFeedback({ success: 'Safe drop recorded successfully!', error: '' });
        console.log(' SafeDropForm: Safe drop submission completed successfully');
        
        // Reset form
        setDenominationCounts(DENOMINATIONS.reduce((acc, denom) => ({ ...acc, [denom.value]: 0 }), {}));
        setNotes('');
        clearSignature();
      } else {
        console.error(' SafeDropForm: API returned success:false', result.error);
        setSubmitFeedback({ error: result.error || 'Failed to record safe drop', success: '' });
        
        // Log API error
        loggingService.logError(
          'safe_drop_create_drawer_count_error',
          `Failed to create drawer count for shift ${selectedShiftId}: ${result.error || 'Unknown error'}`,
          null,
          {
            endpoint: '/cash/drawers',
            method: 'POST',
            shiftId: selectedShiftId,
            totalAmount: totalCountedCash,
          }
        ).catch(logError => {
          console.warn('[SafeDropForm] Failed to log error:', logError);
        });
      }
    } catch (error) {
      console.error(' SafeDropForm: Error in handleSubmit:', error);
      console.error(' SafeDropForm: Error details:', {
        message: error.message,
        stack: error.stack,
        endpoint: error.endpoint,
        status: error.status,
        responseData: error.responseData,
      });
      setSubmitFeedback({ error: error.message || 'An error occurred', success: '' });
      
      // Log API error
      loggingService.logError(
        'safe_drop_submit_error',
        `Failed to submit safe drop for shift ${selectedShiftId}: ${error.message || 'Unknown error'}`,
        error,
        {
          endpoint: '/cash/drawers',
          method: 'POST',
          shiftId: selectedShiftId,
          totalAmount: totalCountedCash,
        }
      ).catch(logError => {
        console.warn('[SafeDropForm] Failed to log error:', logError);
      });
    } finally {
      setIsSubmitting(false);
      console.log(' SafeDropForm: Submit process completed');
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Digital Safe Drop</h1>
        <p className="text-gray-600">Record cash denominations and manager signature for safe drop</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current shift display (from API – read-only) */}
        <Card className="p-6">
          {selectedShiftId && shifts.length > 0 ? (
            (() => {
              const shift = shifts.find((s) => s.ss_id === selectedShiftId) || shifts[0];
              const siteName =
                shift?.site_name ||
                sites.find((s) => (s.site_id || s.id) === (shift?.site_id || selectedSite))?.site_name ||
                sites.find((s) => (s.site_id || s.id) === (shift?.site_id || selectedSite))?.name ||
                'Unknown Site';
              const startTime = formatTimeFromTimestamp(shift?.shift_start_timestamp);
              const endTime = formatTimeFromTimestamp(shift?.shift_end_timestamp);
              return (
                <p className="text-gray-900 font-medium">
                  Shift at &quot;{siteName}&quot; from {startTime} to {endTime}
                </p>
              );
            })()
          ) : (
            <p className="text-gray-500">No current shift. Please ensure you have an active shift from attendance.</p>
          )}
        </Card>

        {/* Expected Cash Display */}
        {expectedCash > 0 && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Expected Cash:</span>
              <span className="text-lg font-bold text-blue-700">₹{expectedCash.toFixed(2)}</span>
            </div>
          </Card>
        )}

        {/* Denomination Grid */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Cash Denominations</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {DENOMINATIONS.map((denom) => (
              <div key={denom.value} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {denom.label}
                </label>
                <input
                  type="number"
                  min="0"
                  value={denominationCounts[denom.value] || 0}
                  onChange={(e) => {
                    const count = parseInt(e.target.value) || 0;
                    setDenominationCounts(prev => ({
                      ...prev,
                      [denom.value]: count,
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <div className="text-xs text-gray-500">
                  = ₹{(denom.value * (denominationCounts[denom.value] || 0)).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">Total Counted Cash:</span>
              <span className="text-2xl font-bold text-green-700">₹{totalCountedCash.toFixed(2)}</span>
            </div>
            {expectedCash > 0 && (
              <div className="mt-2 flex justify-between items-center">
                <span className="text-sm text-gray-600">Variance:</span>
                <span className={`text-lg font-semibold ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {variance >= 0 ? '+' : ''}₹{variance.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Notes */}
        <Card className="p-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Add any notes or comments..."
          />
        </Card>

        {/* Manager Signature */}
        {/* <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Manager Signature</h2>
          
          <div className="mb-4 flex gap-4">
            <button
              type="button"
              onClick={() => setSignatureType('draw')}
              className={`px-4 py-2 rounded-lg font-medium ${
                signatureType === 'draw'
                  ? 'bg-green-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Draw Signature
            </button>
            <button
              type="button"
              onClick={() => setSignatureType('type')}
              className={`px-4 py-2 rounded-lg font-medium ${
                signatureType === 'type'
                  ? 'bg-green-700 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Type Name
            </button>
          </div>

          {signatureType === 'draw' ? (
            <div className="space-y-4">
              <div className="border-2 border-gray-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const mouseEvent = new MouseEvent('mousedown', {
                      clientX: touch.clientX,
                      clientY: touch.clientY,
                    });
                    canvasRef.current.dispatchEvent(mouseEvent);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const mouseEvent = new MouseEvent('mousemove', {
                      clientX: touch.clientX,
                      clientY: touch.clientY,
                    });
                    canvasRef.current.dispatchEvent(mouseEvent);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    stopDrawing();
                  }}
                  className="w-full border border-gray-200 rounded cursor-crosshair"
                  style={{ touchAction: 'none' }}
                />
              </div>
              <button
                type="button"
                onClick={clearSignature}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Clear Signature
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                type="text"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Enter manager name for digital signature"
                className="w-full"
              />
              <button
                type="button"
                onClick={clearSignature}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Clear
              </button>
            </div>
          )}
        </Card> */}

        {/* Feedback Messages */}
        {submitFeedback.error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{submitFeedback.error}</span>
          </div>
        )}

        {submitFeedback.success && (
          <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            <span>{submitFeedback.success}</span>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <PrimaryButton
            type="submit"
            disabled={isSubmitting || !selectedSite || !selectedShiftId}
            className="w-full md:w-auto"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Safe Drop'}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};

export default SafeDropForm;
